import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, LinearProgress, Tooltip,
} from '@mui/material';
import { Timeline as TimelineIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { differenceInDays, format } from 'date-fns';
import api from '../services/api';
import { setProjectPhase } from '../services/api';

const PHASE_NAMES = ['Proposal', 'Engineering & Kick-off', 'Rough-in', 'Pre-Final', 'Final', 'Final Billing', 'AfterCare'];
const PHASE_COLORS = ['#78909c', '#1565c0', '#e65100', '#7b1fa2', '#2e7d32', '#c62828', '#00695c'];
const fmtCurrency = (v) => { const n = parseFloat(v) || 0; return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${n.toFixed(0)}`; };
const fmtDate = (d) => d ? format(new Date(d), 'MMM d') : '';

export default function CompanyTimeline() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewBy, setViewBy] = useState('phase');
  const [sort, setSort] = useState({ col: 'total_proposal_value', dir: 'desc' });

  const handlePhaseChange = async (projectId, newPhase, projectName) => {
    try {
      await setProjectPhase(projectId, newPhase);
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, current_phase: newPhase } : p));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change phase');
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get('/projects', { params: { approved: 'true' } })
      .then(r => setProjects(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }));

  // Enriched projects
  const enriched = useMemo(() => projects.map(p => ({
    ...p,
    daysActive: p.started_at ? differenceInDays(new Date(), new Date(p.started_at)) : 0,
    value: parseFloat(p.total_proposal_value) || 0,
  })), [projects]);

  // Group by view
  const grouped = useMemo(() => {
    const groups = {};
    for (const p of enriched) {
      let key;
      if (viewBy === 'phase') key = PHASE_NAMES[p.current_phase - 1] || 'Unknown';
      else if (viewBy === 'pm') key = p.pm_name || 'Unassigned';
      else if (viewBy === 'sales') key = p.sales_name || p.sales_rep_name || 'Unassigned';
      else if (viewBy === 'health') key = (p.health_status || 'unknown').charAt(0).toUpperCase() + (p.health_status || 'unknown').slice(1);
      else key = p.job_type || 'No Type';
      if (!groups[key]) groups[key] = { name: key, projects: [], value: 0, count: 0 };
      groups[key].projects.push(p);
      groups[key].value += p.value;
      groups[key].count++;
    }
    // Sort groups
    return Object.values(groups).sort((a, b) => {
      if (viewBy === 'phase') {
        const ai = PHASE_NAMES.indexOf(a.name);
        const bi = PHASE_NAMES.indexOf(b.name);
        return ai - bi;
      }
      return b.value - a.value;
    });
  }, [enriched, viewBy]);

  // Totals
  const totalValue = enriched.reduce((s, p) => s + p.value, 0);
  const totalRed = enriched.filter(p => p.health_status === 'red').length;
  const totalYellow = enriched.filter(p => p.health_status === 'yellow').length;

  const sl = (col, label) => (
    <TableSortLabel active={sort.col === col} direction={sort.col === col ? sort.dir : 'asc'} onClick={() => handleSort(col)}
      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}>{label}</TableSortLabel>
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>Company Timeline</Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            {enriched.length} active projects · {fmtCurrency(totalValue)} total pipeline · {totalRed} red · {totalYellow} yellow
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Group By</InputLabel>
          <Select value={viewBy} label="Group By" onChange={e => setViewBy(e.target.value)}>
            <MenuItem value="phase">Phase</MenuItem>
            <MenuItem value="pm">Project Manager</MenuItem>
            <MenuItem value="sales">Sales Rep</MenuItem>
            <MenuItem value="health">Health Status</MenuItem>
            <MenuItem value="type">Job Type</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Group summary cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {grouped.map((g, i) => {
          const pctOfTotal = totalValue > 0 ? (g.value / totalValue * 100) : 0;
          const red = g.projects.filter(p => p.health_status === 'red').length;
          const color = viewBy === 'phase' ? (PHASE_COLORS[PHASE_NAMES.indexOf(g.name)] || '#616161')
            : viewBy === 'health' ? (g.name === 'Green' ? '#2e7d32' : g.name === 'Yellow' ? '#f57c00' : g.name === 'Red' ? '#c62828' : '#616161')
            : '#0D2C54';
          return (
            <Grid item xs={6} sm={4} md={3} lg={2} key={g.name}>
              <Paper elevation={1} sx={{ p: 1.5, borderLeft: `4px solid ${color}`, height: '100%' }}>
                <Typography variant="caption" fontWeight="bold" color={color} noWrap>{g.name}</Typography>
                <Typography variant="h5" fontWeight="bold">{g.count}</Typography>
                <Typography variant="caption" color="text.secondary">{fmtCurrency(g.value)}</Typography>
                {red > 0 && <Chip label={`${red} red`} size="small" sx={{ ml: 0.5, bgcolor: '#ffebee', color: '#c62828', fontSize: '0.65rem', height: 18 }} />}
                <LinearProgress variant="determinate" value={pctOfTotal} sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Grouped project tables */}
      {grouped.map(g => {
        const sorted2 = [...g.projects].sort((a, b) => {
          let av = a[sort.col], bv = b[sort.col];
          if (['total_proposal_value', 'daysActive', 'current_phase'].includes(sort.col)) { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
          else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
          return sort.dir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
        });
        const groupColor = viewBy === 'phase' ? (PHASE_COLORS[PHASE_NAMES.indexOf(g.name)] || '#616161') : '#0D2C54';
        return (
          <Paper key={g.name} elevation={2} sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: groupColor, color: '#fff', px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography fontWeight="bold">{g.name}</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2">{g.count} projects</Typography>
                <Typography variant="body2" fontWeight="bold">{fmtCurrency(g.value)}</Typography>
              </Box>
            </Box>
            <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: '#f5f5f5', '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                  <TableCell>Job #</TableCell>
                  <TableCell>Project</TableCell>
                  {viewBy !== 'pm' && <TableCell>PM</TableCell>}
                  {viewBy !== 'sales' && <TableCell>Sales</TableCell>}
                  <TableCell>Phase</TableCell>
                  {viewBy !== 'health' && <TableCell>Health</TableCell>}
                  <TableCell align="center">Days Active</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {sorted2.map((p, i) => (
                    <TableRow key={p.id} hover onClick={() => navigate(`/projects/${p.id}`)} sx={{ cursor: 'pointer', bgcolor: i % 2 ? 'action.hover' : undefined }}>
                      <TableCell><Typography variant="body2" color="primary.main" fontWeight="bold">{p.primary_job_number || '—'}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 250 }}>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.client_name}</Typography>
                      </TableCell>
                      {viewBy !== 'pm' && <TableCell><Typography variant="caption">{p.pm_name || '—'}</Typography></TableCell>}
                      {viewBy !== 'sales' && <TableCell><Typography variant="caption">{p.sales_name || p.sales_rep_name || '—'}</Typography></TableCell>}
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Select size="small" value={p.current_phase}
                          onChange={e => { e.stopPropagation(); handlePhaseChange(p.id, e.target.value, p.name); }}
                          onClick={e => e.stopPropagation()}
                          sx={{ fontSize: '0.75rem', fontWeight: 600, height: 28, minWidth: 120, '.MuiSelect-select': { py: 0.3 } }}>
                          {PHASE_NAMES.map((n, i) => <MenuItem key={i+1} value={i+1} sx={{ fontSize: '0.75rem' }}>{i+1}. {n}</MenuItem>)}
                        </Select>
                      </TableCell>
                      {viewBy !== 'health' && <TableCell><Chip label={p.health_status} size="small" sx={{ textTransform: 'capitalize', bgcolor: p.health_status === 'green' ? '#e8f5e9' : p.health_status === 'yellow' ? '#fff3e0' : '#ffebee', color: p.health_status === 'green' ? '#2e7d32' : p.health_status === 'yellow' ? '#e65100' : '#c62828', fontWeight: 600, fontSize: '0.65rem' }} /></TableCell>}
                      <TableCell align="center">
                        <Typography variant="body2" color={p.daysActive > 90 ? 'error.main' : p.daysActive > 60 ? 'warning.main' : 'text.primary'}>
                          {p.daysActive || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={p.value > 0 ? 'bold' : 'normal'} color={p.value > 0 ? 'success.main' : 'text.secondary'}>
                          {p.value > 0 ? fmtCurrency(p.value) : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
    </Box>
  );
}
